import { z } from "zod";

// ─── next-auth module augmentation ──────────────────────────────────────────

declare module "next-auth" {
  interface Session {
    user: {
      id?: string;
      name?: string | null;
      email?: string | null;
      username?: string;
      role?: string;
      organisationId?: number | string;
      fullName?: string | null;
      businessName?: string | null;
      businessEmail?: string | null;
      phoneNumber?: string | null;
      accountantEmail?: string | null;
      /** Optional account ID from the login payload (used e.g. for CSV upload). */
      accountId?: number;
      /** Whether the user has completed the onboarding flow. */
      onboarded?: boolean;
      /** Whether the user has verified their business email. */
      emailVerified?: boolean;
      /** Whether the user has verified their phone number. */
      phoneNumberVerified?: boolean;
      /** Whether the user has an active subscription. */
      hasSubscription?: boolean;
      /** The user's subscription status. */
      subscriptionStatus?: 'ACTIVE' | 'INACTIVE' | 'CANCELED_AT_PERIOD_END' | 'TRIALING' | 'PAST_DUE' | 'CANCELED';
    };
    error?: string;
  }

  interface User {
    id: string;
    username?: string;
    email?: string;
    role?: string;
    organisationId?: number | string;
    accessToken?: string;
    refreshToken?: string;
    accessTokenExpiresIn?: number;
    refreshTokenExpiresIn?: number;
    fullName?: string;
    businessName?: string;
    businessEmail?: string;
    phoneNumber?: string;
    accountantEmail?: string;
    /** Optional account ID from the login payload. */
    accountId?: number;
    /** Whether the user has completed onboarding. */
    onboarded?: boolean;
    /** Whether the user has verified their business email. */
    emailVerified?: boolean;
    /** Whether the user has verified their phone number. */
    phoneNumberVerified?: boolean;
    /** Whether the user has an active subscription. */
    hasSubscription?: boolean;
    /** The user's subscription status. */
    subscriptionStatus?: 'ACTIVE' | 'INACTIVE' | 'CANCELED_AT_PERIOD_END' | 'TRIALING' | 'PAST_DUE' | 'CANCELED';
  }
}

declare module "next-auth/jwt" {
  interface JWT {
    id?: string;
    username?: string;
    email?: string;
    role?: string;
    organisationId?: number | string;
    accessToken?: string;
    refreshToken?: string;
    accessTokenExpiresAt?: number;
    refreshTokenExpiresAt?: number;
    fullName?: string;
    businessName?: string;
    businessEmail?: string;
    phoneNumber?: string;
    accountantEmail?: string;
    /** Optional account ID from the login payload. */
    accountId?: number;
    /** Whether the user has completed onboarding. */
    onboarded?: boolean;
    /** Whether the user has verified their business email. */
    emailVerified?: boolean;
    /** Whether the user has verified their phone number. */
    phoneNumberVerified?: boolean;
    /** Whether the user has an active subscription. */
    hasSubscription?: boolean;
    /** The user's subscription status. */
    subscriptionStatus?: 'ACTIVE' | 'INACTIVE' | 'CANCELED_AT_PERIOD_END' | 'TRIALING' | 'PAST_DUE' | 'CANCELED';
    error?: string;
  }
}

// ─── Zod Schemas ─────────────────────────────────────────────────────────────

export const SignInSchema = z.object({
  username: z
    .string({ message: "Username is required." })
    .min(2, { message: "Username must be at least 2 characters." })
    .trim(),
  password: z
    .string({ message: "Password is required." })
    .min(6, { message: "Password must be at least 6 characters." }),
});

/**
 * Sign-Up schema — matches POST /api/v1/auth/register
 * Step 1: username, password
 * Step 2: businessName, businessEmail, businessPhoneNumber, vatScheme, accountantEmail (optional), referralSource (optional)
 */
export const SignUpSchema = z.object({
  fullName: z
    .string({ message: "Full name is required." })
    .min(2, { message: "Full name must be at least 2 characters." })
    .trim(),
  username: z
    .string({ message: "Username is required." })
    .min(2, { message: "Username must be at least 2 characters." })
    .trim(),
  password: z
    .string({ message: "Password is required." })
    .min(12, { message: "Password must be at least 12 characters." })
    .max(128, { message: "Password must not exceed 128 characters." }),
  organisationId: z.string().optional(),
  role: z.string().optional(),
  email: z
    .string()
    .email("Please enter a valid email address.")
    .trim()
    .optional(),
  businessName: z
    .string({ message: "Business name is required." })
    .min(2, { message: "Business name must be at least 2 characters." })
    .trim(),
  businessEmail: z
    .string()
    .email("Please enter a valid business email address.")
    .trim(),
  businessPhoneNumber: z
    .string({ message: "Business phone number is required." })
    .min(10, { message: "Please enter a valid phone number." })
    .trim(),
  vatScheme: z.string().min(1, {
    message: "Please select a VAT scheme.",
  }),
  accountantEmail: z
    .string()
    .email("Please enter a valid accountant email.")
    .optional()
    .or(z.literal("")),
  referralSource: z.string().optional(),
});

export const SignUpStep1Schema = SignUpSchema.pick({
  fullName: true,
  username: true,
  password: true,
  referralSource: true,
});

export const SignUpFormSchema = SignUpSchema.extend({
  agreedToTerms: z.boolean().refine((value) => value, {
    message: "You must accept the terms and conditions.",
  }),
});

// ─── Forgot Password Schemas ──────────────────────────────────────────────────

export const ForgotPasswordSchema = z.object({
  email: z
    .string({ message: "Email is required." })
    .email("Please enter a valid email address.")
    .trim(),
});

export const ResetPasswordSchema = z
  .object({
    newPassword: z
      .string({ message: "Password is required." })
      .min(12, { message: "Password must be at least 12 characters." })
      .max(128, { message: "Password must not exceed 128 characters." }),
    confirmPassword: z.string({ message: "Please confirm your password." }),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match.",
    path: ["confirmPassword"],
  });

export type SignInInput = z.infer<typeof SignInSchema>;
export type SignUpInput = z.infer<typeof SignUpSchema>;
export type SignUpStep1Values = z.infer<typeof SignUpStep1Schema>;
export type SignUpFormValues = z.infer<typeof SignUpFormSchema>;
export type ForgotPasswordInput = z.infer<typeof ForgotPasswordSchema>;
export type ResetPasswordInput = z.infer<typeof ResetPasswordSchema>;

// ─── API response types ───────────────────────────────────────────────────────

export type Organisation = {
  id: number;
  name: string;
};

export type BackendAuthResponse = {
  status?: string;
  message?: string;
  data?: {
    id?: string;
    token?: string;
    access_token?: string;
    accessToken?: string;
    refreshToken?: string;
    accessTokenExpiresIn?: number;
    refreshTokenExpiresIn?: number;
    fullName?: string;
    username: string;
    role: string;
    organisationId?: string | number;
    organisation?: number;
    email?: string;
    businessName?: string;
    businessEmail?: string;
    phoneNumber?: string;
    accountId?: number;
    onboarded?: boolean;
    emailVerified?: boolean;
    phoneNumberVerified?: boolean;
    accountantEmail?: string;
    hasSubscription?: boolean;
    subscriptionStatus?: 'ACTIVE' | 'INACTIVE' | 'CANCELED_AT_PERIOD_END' | 'TRIALING' | 'PAST_DUE' | 'CANCELED';
  };
  token?: string;
  accessToken?: string;
  access_token?: string;
  refreshToken?: string;
  accessTokenExpiresIn?: number;
  refreshTokenExpiresIn?: number;
  organisationId?: number | string;
  organisation?: number | string;
  onboarded?: boolean;
  emailVerified?: boolean;
  phoneNumberVerified?: boolean;
  phoneNumber?: string;
  userId?: string | number;
  id?: string | number;
  fullName?: string;
  username?: string;
  email?: string;
  businessEmail?: string;
  businessName?: string;
  accountantEmail?: string;
  role?: string;
  hasSubscription?: boolean;
  subscriptionStatus?: 'ACTIVE' | 'INACTIVE' | 'CANCELED_AT_PERIOD_END' | 'TRIALING' | 'PAST_DUE' | 'CANCELED';
};

export type RefreshTokenResponse = {
  status?: string;
  message?: string;
  data?: {
    accessToken?: string;
    refreshToken?: string;
    accessTokenExpiresIn?: number;
    refreshTokenExpiresIn?: number;
  };
};

export type PasswordResetValidateResponse = {
  status?: string;
  message?: string;
  valid?: boolean;
  data?: {
    valid?: boolean;
  };
};

export const REFERRAL_SOURCE_OPTIONS = [
  "Google Search",
  "Social Media",
  "Accountant",
  "Friend or Colleague",
  "Online Ad",
  "Other",
] as const;

export const VAT_SCHEME_OPTIONS = [
  { value: "Standard", label: "Standard rate (20%)" },
  { value: "Flat Rate", label: "Flat Rate Scheme" },
  { value: "Cash Accounting", label: "Cash Accounting Scheme" },
] as const;

export type ReferralSource = (typeof REFERRAL_SOURCE_OPTIONS)[number];
export type VatScheme = "Standard" | "Flat Rate" | "Cash Accounting";
